import { useEffect, useState } from 'react';
import { PuffLoader } from 'react-spinners';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Unified loading card used across the lesson page. Replaces every
 * bespoke loading visual on this surface - per-panel skeletons, the
 * OnDemandWrapper "Generating…" view, the chat panel's bar shimmer,
 * the sources panel's literal "Loading lesson…" text.
 *
 * Visual rules:
 *   - Single centered spinner (PuffLoader) at a neutral grey
 *   - Lucide icon overlaid in the spinner center - carries per-panel
 *     identity (Text → FileText, Mindmap → Brain, etc.)
 *   - Title says what is being prepared, e.g. "Preparing Text"
 *     (`processing`) or "Queued - Text" (`pending`).
 *   - Optional 1-line description + optional small hint tag below
 *
 * Spacing matches `PanelGenerate` and `PanelFallback` so the three
 * states share identical proportions - only the content swaps when
 * transitioning between them.
 */

export interface PanelLoadingProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  className?: string;
}

export function PanelLoading({
  icon: Icon,
  title,
  description,
  className,
}: PanelLoadingProps) {
  // `PuffLoader` needs a literal color, not a Tailwind class, so `dark:`
  // can't reach it. Resolve the applied theme (the `.dark` class the theme
  // provider sets on <html>) and pick lavender (--primary ≈ rgb 172 139 249)
  // in dark, brand-500 in light. The observer keeps it correct if the user
  // toggles theme while a spinner is on screen.
  const [isDark, setIsDark] = useState(
    () =>
      typeof document !== 'undefined' &&
      document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const el = document.documentElement;
    const sync = () => setIsDark(el.classList.contains('dark'));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  const spinnerColor = isDark
    ? 'rgb(172 139 249 / 0.8)'
    : 'rgb(99 102 241 / 0.8)';

  return (
    <div
      className={cn(
        'animate-fade-in-up flex h-full w-full flex-col items-center justify-center px-6 py-12 text-center',
        className
      )}
    >
      <div className='relative flex h-20 w-20 items-center justify-center'>
        <PuffLoader color={spinnerColor} size={80} speedMultiplier={0.8} />
        <Icon className='absolute h-7 w-7 text-brand-500/80 dark:text-primary' />
      </div>
      {/* A status line, not a document heading. It was an `<h3>`, which reads
          as structure it does not have: the panels carry no `<h2>`, so under
          the page's `<h1>` it skipped a level and axe flagged `heading-order`
          the moment a loading panel and the mobile header were on screen
          together. `role='status'` is also the more useful announcement —
          "Preparing Text" is a live update, and a screen reader should hear it
          when it changes rather than only when navigating by heading. */}
      <p role='status' className='mt-4 text-base font-medium text-foreground'>
        {title}
      </p>
      {description && (
        <p className='mt-1 max-w-sm text-sm text-muted-foreground'>
          {description}
        </p>
      )}
    </div>
  );
}
